import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import {
  adminUsers,
  adminRoles,
  type AdminUser,
  type UserPatch,
} from "@/api/adminPanel";
import { Modal } from "@/components/Modal";
import { Badge, Button, Card, CardBody, Input, Label } from "@/components/ui";
import { useToast } from "@/components/Toast";

export function AdminUsersPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const list = useQuery({ queryKey: ["admin-users"], queryFn: () => adminUsers.list() });
  const [rolesTarget, setRolesTarget] = useState<AdminUser | null>(null);
  const [editTarget, setEditTarget] = useState<AdminUser | null>(null);

  const setActive = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      adminUsers.setActive(id, is_active),
    onSuccess: (u) => {
      toast.push(`${u.email} ${u.is_active ? "activated" : "deactivated"}`, "success");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (e) =>
      toast.push(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e), "error"),
  });

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Users</h1>
      <Card>
        <CardBody className="overflow-x-auto">
          {list.isLoading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : list.data?.length === 0 ? (
            <p className="text-sm text-slate-500">No users in this org yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="py-2">Email</th>
                  <th>Name</th>
                  <th>Roles</th>
                  <th>MFA</th>
                  <th>KYC</th>
                  <th>Status</th>
                  <th>Last login</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.data?.map((u: AdminUser) => (
                  <tr key={u.id} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="py-2 font-medium">{u.email}</td>
                    <td className="text-xs text-slate-500">{u.full_name ?? "—"}</td>
                    <td className="text-xs">
                      <div className="flex flex-wrap gap-1">
                        {u.is_superuser && <Badge tone="good">super_admin</Badge>}
                        {u.roles.map((r) => (
                          <Badge key={r}>{r}</Badge>
                        ))}
                      </div>
                    </td>
                    <td>{u.totp_enabled ? <Badge tone="good">on</Badge> : <Badge>off</Badge>}</td>
                    <td>{kycBadge(u.kyc_status)}</td>
                    <td>
                      {u.is_active ? <Badge tone="good">active</Badge> : <Badge tone="bad">disabled</Badge>}
                    </td>
                    <td className="text-xs text-slate-500">
                      {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : "never"}
                    </td>
                    <td className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" onClick={() => setEditTarget(u)}>
                          Edit
                        </Button>
                        <Button variant="ghost" onClick={() => setRolesTarget(u)}>
                          Roles
                        </Button>
                        <Button
                          variant={u.is_active ? "danger" : "primary"}
                          onClick={() => setActive.mutate({ id: u.id, is_active: !u.is_active })}
                        >
                          {u.is_active ? "Deactivate" : "Activate"}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {rolesTarget && (
        <RolesModal user={rolesTarget} onClose={() => setRolesTarget(null)} />
      )}
      {editTarget && (
        <EditUserModal user={editTarget} onClose={() => setEditTarget(null)} />
      )}
    </div>
  );
}

function EditUserModal({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [email, setEmail] = useState(user.email);
  const [fullName, setFullName] = useState(user.full_name ?? "");
  const [isVerified, setIsVerified] = useState<boolean>(user.is_verified);

  const save = useMutation({
    mutationFn: () => {
      const body: UserPatch = {};
      if (email.trim().toLowerCase() !== user.email.toLowerCase()) body.email = email.trim();
      if ((fullName.trim() || null) !== (user.full_name ?? null)) body.full_name = fullName.trim();
      if (isVerified !== user.is_verified) body.is_verified = isVerified;
      if (Object.keys(body).length === 0) {
        return Promise.reject(new Error("No changes to save."));
      }
      return adminUsers.patch(user.id, body);
    },
    onSuccess: (u) => {
      toast.push(`${u.email} updated`, "success");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      onClose();
    },
    onError: (e) =>
      toast.push(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e), "error"),
  });

  return (
    <Modal open onClose={onClose} title={`Edit · ${user.email}`}>
      <div className="space-y-3">
        <div>
          <Label htmlFor="edit-email">Email</Label>
          <Input
            id="edit-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="edit-name">Full name</Label>
          <Input
            id="edit-name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isVerified}
            onChange={(e) => setIsVerified(e.target.checked)}
          />
          Email verified
        </label>
        <p className="text-xs text-slate-500">
          Unchecking forces the user to re-verify on next login. Password reset stays
          on the self-serve flow at <code>/forgot-password</code>.
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}


function RolesModal({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const roles = useQuery({ queryKey: ["admin-roles"], queryFn: () => adminRoles.list() });

  const assign = useMutation({
    mutationFn: (role_id: string) => adminUsers.assignRole(user.id, role_id),
    onSuccess: () => {
      toast.push("Role assigned", "success");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (e) =>
      toast.push(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e), "error"),
  });
  const revoke = useMutation({
    mutationFn: (role_id: string) => adminUsers.revokeRole(user.id, role_id),
    onSuccess: () => {
      toast.push("Role revoked", "warn");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (e) =>
      toast.push(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e), "error"),
  });

  const userRoleNames = new Set(user.roles);

  return (
    <Modal open onClose={onClose} title={`Roles · ${user.email}`}>
      <div className="space-y-3">
        {roles.isLoading ? (
          <p className="text-sm text-term-mute">Loading roles…</p>
        ) : !roles.data || roles.data.length === 0 ? (
          <p className="text-sm text-term-mute">No roles defined yet.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-term-elev text-term-mute">
              <tr>
                <th className="px-2 py-1 text-left text-2xs uppercase tracking-wider">Role</th>
                <th className="px-2 py-1 text-left text-2xs uppercase tracking-wider">Permissions</th>
                <th className="px-2 py-1 text-left text-2xs uppercase tracking-wider">Source</th>
                <th className="px-2 py-1 text-right text-2xs uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody>
              {roles.data.map((r) => {
                const has = userRoleNames.has(r.name);
                return (
                  <tr key={r.id} className="border-t border-term-border">
                    <td className="px-2 py-1 font-medium text-term-fg">{r.name}</td>
                    <td className="px-2 py-1">
                      <div className="flex flex-wrap gap-1">
                        {r.permission_keys.slice(0, 6).map((k) => (
                          <Badge key={k}>{k}</Badge>
                        ))}
                        {r.permission_keys.length > 6 && (
                          <Badge>+{r.permission_keys.length - 6} more</Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <Badge tone={r.is_system ? "neutral" : "good"}>
                        {r.is_system ? "system" : "org"}
                      </Badge>
                    </td>
                    <td className="px-2 py-1 text-right">
                      {has ? (
                        <Button
                          variant="danger"
                          onClick={() => revoke.mutate(r.id)}
                          disabled={revoke.isPending}
                        >
                          Revoke
                        </Button>
                      ) : (
                        <Button
                          onClick={() => assign.mutate(r.id)}
                          disabled={assign.isPending}
                        >
                          Assign
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <div className="flex justify-end pt-2">
          <Button variant="ghost" onClick={onClose}>Done</Button>
        </div>
      </div>
    </Modal>
  );
}

function kycBadge(s: string) {
  const tone =
    s === "approved" ? "good" : s === "rejected" || s === "expired" ? "bad" : s === "pending" ? "warn" : "neutral";
  return <Badge tone={tone as "good" | "bad" | "warn" | "neutral"}>{s}</Badge>;
}
